import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { TopBar } from "../components/TopBar";
import { StatusBadge } from "../components/StatusBadge";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../components/ui/select";
import { api, formatRelative, formatDateShort, STATUS_LABELS, STATUS_ORDER, canEditAll } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../context/SettingsContext";
import { ScanLine, Search, Plus, ChevronRight, Flame, Clock, Layers, X, Image as ImageIcon, AlertTriangle, CheckCircle2 } from "lucide-react";
import { CreateRecordDialog } from "../components/CreateRecordDialog";
import { BulkUpdateDialog } from "../components/BulkUpdateDialog";
import { SpecQuickViewDialog } from "../components/SpecQuickViewDialog";
import { ProgressDots } from "../components/ProgressTimeline";

export default function DashboardPage() {
  const { user } = useAuth();
  const settings = useSettings();
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("active"); // "active" hides delivered; "all" shows everything
  const [thirdPartyFilter, setThirdPartyFilter] = useState("all");
  const [thirdParties, setThirdParties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBulk, setShowBulk] = useState(false);
  const [specRecord, setSpecRecord] = useState(null);
  const [kpiFilter, setKpiFilter] = useState(null); // "urgent" | "overdue" | "issue" | "delivered" | null

  const canBulk = canEditAll(user);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (statusFilter && statusFilter !== "all" && statusFilter !== "active") params.status = statusFilter;
      if (thirdPartyFilter && thirdPartyFilter !== "all") params.third_party_id = thirdPartyFilter;
      const [r, s, tp] = await Promise.all([
        api.get("/bath-records", { params }),
        api.get("/reports/summary"),
        api.get("/third-parties"),
      ]);
      setRecords(r.data);
      setSummary(s.data);
      setThirdParties(tp.data);
      // Drop any selections that no longer exist
      setSelectedIds((prev) => {
        const next = new Set();
        const ids = new Set(r.data.map((x) => x.id));
        prev.forEach((id) => ids.has(id) && next.add(id));
        return next;
      });
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, thirdPartyFilter]);

  useEffect(() => {
    load();
  }, [load]);

  // Client-side filters: active (default) hides delivered, KPI quick filter on top of that
  const visibleRecords = useMemo(() => {
    let list = records;
    // Hide delivered when the "Active only" status filter is selected — UNLESS the
    // user has explicitly clicked the "Delivered" KPI (which needs delivered rows).
    if (statusFilter === "active" && kpiFilter !== "delivered") {
      list = list.filter((r) => r.status !== "delivered");
    }
    if (kpiFilter === "urgent") list = list.filter((r) => r.is_urgent && r.status !== "delivered");
    else if (kpiFilter === "issue") list = list.filter((r) => r.status === "issue");
    else if (kpiFilter === "delivered") list = list.filter((r) => r.status === "delivered");
    else if (kpiFilter === "with_third_party") list = list.filter((r) => ["collected", "in_specification"].includes(r.status));
    else if (kpiFilter === "awaiting_collection") list = list.filter((r) => r.status === "awaiting_collection");
    else if (kpiFilter === "ready_to_collect_back") list = list.filter((r) => r.status === "ready_for_return");
    else if (kpiFilter === "received_today") {
      const today = new Date(); today.setHours(0, 0, 0, 0);
      list = list.filter((r) => r.date_received && new Date(r.date_received).getTime() >= today.getTime());
    }
    return list;
  }, [records, statusFilter, kpiFilter]);

  const deliveredHidden = useMemo(
    () => statusFilter === "active" ? records.filter((r) => r.status === "delivered").length : 0,
    [records, statusFilter]
  );

  // Compute which rows are "overdue" using the threshold + mode from the summary endpoint.
  const overdueDays = summary?.overdue_days ?? settings.overdue_days ?? 7;
  const overdueMode = summary?.overdue_mode ?? settings.overdue_mode ?? "days_with_third_party";
  const overdueIds = useMemo(() => {
    const cutoff = Date.now() - overdueDays * 24 * 60 * 60 * 1000;
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    const ids = new Set();
    records.forEach((r) => {
      if (!["collected", "in_specification"].includes(r.status)) return;
      let byDays = false;
      let byExpected = false;
      if (r.date_collected) {
        const t = new Date(r.date_collected).getTime();
        if (!Number.isNaN(t) && t <= cutoff) byDays = true;
      }
      if (r.expected_return_date) {
        const er = new Date(r.expected_return_date);
        if (!Number.isNaN(er.getTime())) {
          const erDay = new Date(er);
          erDay.setHours(0, 0, 0, 0);
          if (todayStart.getTime() > erDay.getTime()) byExpected = true;
        }
      }
      if (overdueMode === "days_with_third_party" && byDays) ids.add(r.id);
      else if (overdueMode === "past_expected_return" && byExpected) ids.add(r.id);
      else if (overdueMode === "either" && (byDays || byExpected)) ids.add(r.id);
    });
    return ids;
  }, [records, overdueDays, overdueMode]);

  const overdueHint =
    overdueMode === "past_expected_return"
      ? "Past expected back date"
      : overdueMode === "either"
      ? `> ${overdueDays}d or past expected back`
      : `> ${overdueDays} days with third party`;

  const kpis = [
    { key: "received_today", label: "Received Today", value: summary?.received_today ?? 0, hint: "Goods-in today" },
    { key: "awaiting_collection", label: "Awaiting Collection", value: summary?.awaiting_collection ?? 0, hint: "Ready for Giavani" },
    { key: "with_third_party", label: "With Third Party", value: summary?.with_third_party ?? 0, hint: "Collected + In spec" },
    { key: "ready_to_collect_back", label: "Ready to Collect Back", value: summary?.ready_to_collect_back ?? 0, hint: "Giavani finished", tone: "fuchsia" },
    { key: "urgent", label: "Urgent", value: summary?.urgent ?? 0, hint: "Flagged urgent", tone: "amber", icon: Flame },
    { key: "overdue", label: "Overdue", value: summary?.overdue ?? 0, hint: overdueHint, tone: "amber", icon: Clock },
    { key: "issue", label: "Issues", value: summary?.issue ?? 0, hint: "Status = Issue / Query", tone: "rose" },
    { key: "delivered", label: "Delivered", value: summary?.delivered ?? 0, hint: "Reached the customer", tone: "emerald", icon: CheckCircle2 },
  ];

  const toggle = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelectedIds((prev) => {
      if (prev.size === records.length) return new Set();
      return new Set(records.map((r) => r.id));
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  return (
    <div className="min-h-screen bg-zinc-50">
      <TopBar onSync={load} />

      <main className="mx-auto max-w-[1600px] px-4 sm:px-6 lg:px-8 py-6">
        {/* Heading */}
        <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tighter text-zinc-950">
              Operations Dashboard
            </h1>
            <p className="text-sm text-zinc-600 mt-1">
              Tracking every Whirlpool bath from receipt to dispatch.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => navigate("/scanner")}
              className="bg-zinc-950 hover:bg-zinc-800 text-white"
              data-testid="scan-barcode-button"
            >
              <ScanLine className="h-4 w-4 mr-2" /> Scan / Lookup
            </Button>
            {canEditAll(user) && (
              <Button
                variant="outline"
                onClick={() => setShowCreate(true)}
                className="border-zinc-200"
                data-testid="create-record-button"
              >
                <Plus className="h-4 w-4 mr-2" /> New Record
              </Button>
            )}
          </div>
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-2 lg:grid-cols-8 gap-3 mb-6">
          {kpis.map((k) => {
            const Tone = k.tone;
            const active = k.value > 0;
            const isActiveFilter = kpiFilter === k.key;
            const border = isActiveFilter
              ? Tone === "rose" ? "border-rose-400 ring-2 ring-rose-200"
                : Tone === "amber" ? "border-amber-400 ring-2 ring-amber-200"
                : Tone === "emerald" ? "border-emerald-400 ring-2 ring-emerald-200"
                : Tone === "fuchsia" ? "border-fuchsia-400 ring-2 ring-fuchsia-200"
                : "border-zinc-900 ring-2 ring-zinc-200"
              : Tone === "rose" && active ? "border-rose-200"
              : Tone === "amber" && active ? "border-amber-200"
              : Tone === "emerald" && active ? "border-emerald-200"
              : Tone === "fuchsia" && active ? "border-fuchsia-200"
              : "border-zinc-200";
            const valueClr = Tone === "rose" && active ? "text-rose-600"
              : Tone === "amber" && active ? "text-amber-600"
              : Tone === "emerald" && active ? "text-emerald-600"
              : Tone === "fuchsia" && active ? "text-fuchsia-600"
              : "text-zinc-950";
            const handleClick = () => {
              // Toggle the KPI filter. Do NOT touch statusFilter — the outer
              // "active hides delivered" gate is bypassed for kpi=delivered
              // in visibleRecords, so we can preserve the user's chosen
              // status filter across KPI toggles.
              setKpiFilter((prev) => (prev === k.key ? null : k.key));
            };
            return (
              <button
                key={k.key}
                type="button"
                onClick={handleClick}
                data-testid={`kpi-${k.key}`}
                className={`text-left bg-white border ${border} rounded-md p-4 shadow-sm transition-all duration-150 hover:shadow-md ${isActiveFilter ? "translate-y-[1px] shadow-inner" : "active:translate-y-[1px]"}`}
              >
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold flex items-center gap-1.5">
                  {k.icon && <k.icon className="h-3 w-3" />} {k.label}
                </div>
                <div className={`font-heading text-4xl font-extrabold tracking-tighter mt-1 ${valueClr}`}>
                  {k.value}
                </div>
                <div className="text-xs text-zinc-500 mt-1">{k.hint}</div>
              </button>
            );
          })}
        </div>

        {/* Filters */}
        <div className="bg-white border border-zinc-200 rounded-md p-3 mb-4 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search PO (or revised), SO, SKU, customer, barcode…"
              className="pl-9"
              data-testid="dashboard-search"
            />
          </div>
          <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setKpiFilter(null); }}>
            <SelectTrigger className="w-[220px]" data-testid="status-filter">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active only (hide delivered)</SelectItem>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUS_ORDER.map((s) => (
                <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={thirdPartyFilter} onValueChange={setThirdPartyFilter}>
            <SelectTrigger className="w-[200px]" data-testid="thirdparty-filter">
              <SelectValue placeholder="All third parties" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All third parties</SelectItem>
              {thirdParties.map((tp) => (
                <SelectItem key={tp.id} value={tp.id}>{tp.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {kpiFilter && (
            <button
              type="button"
              onClick={() => setKpiFilter(null)}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-zinc-700 bg-zinc-100 hover:bg-zinc-200 transition-colors rounded-full px-3 py-1.5"
              data-testid="clear-kpi-filter"
              title="Clear quick filter"
            >
              Filtered by: {kpis.find((k) => k.key === kpiFilter)?.label || kpiFilter}
              <X className="h-3 w-3" />
            </button>
          )}
        </div>

        {/* Bulk-action bar */}
        {canBulk && selectedIds.size > 0 && (
          <div
            className="sticky top-2 z-30 mb-3 flex flex-wrap items-center gap-3 rounded-md border border-zinc-900 bg-zinc-950 text-white shadow-lg px-4 py-2.5"
            data-testid="bulk-action-bar"
          >
            <Layers className="h-4 w-4" />
            <div className="text-sm font-semibold" data-testid="bulk-selected-count">
              {selectedIds.size} selected
            </div>
            <div className="text-xs text-zinc-400 hidden sm:block">Apply the same change to all of them.</div>
            <div className="ml-auto flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                className="border-zinc-700 text-white hover:bg-zinc-800 hover:text-white"
                onClick={clearSelection}
                data-testid="bulk-clear"
              >
                <X className="h-3.5 w-3.5 mr-1" /> Clear
              </Button>
              <Button
                size="sm"
                className="bg-white text-zinc-950 hover:bg-zinc-100"
                onClick={() => setShowBulk(true)}
                data-testid="bulk-open"
              >
                Bulk update →
              </Button>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="bg-white border border-zinc-200 rounded-md shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50">
                <tr className="text-[10px] uppercase tracking-wider text-zinc-500">
                  {canBulk && (
                    <th className="w-9 pl-4 py-3">
                      <Checkbox
                        checked={records.length > 0 && selectedIds.size === records.length}
                        onCheckedChange={toggleAll}
                        data-testid="bulk-select-all"
                        aria-label="Select all"
                      />
                    </th>
                  )}
                  <th className="text-left py-3 px-4 font-semibold">
                    {settings.supplier_name} PO
                    <div className="text-[9px] text-zinc-400 normal-case tracking-normal font-normal mt-0.5">Plain bath supplier</div>
                  </th>
                  <th className="text-left py-3 px-4 font-semibold">
                    Giavani PO
                    <div className="text-[9px] text-zinc-400 normal-case tracking-normal font-normal mt-0.5">Our PO / SO</div>
                  </th>
                  <th className="text-left py-3 px-4 font-semibold">Product</th>
                  <th className="text-left py-3 px-4 font-semibold">Customer</th>
                  <th className="text-left py-3 px-4 font-semibold">Status</th>
                  <th className="text-center py-3 px-2 font-semibold w-12" title="Spec uploaded">
                    <ImageIcon className="h-3.5 w-3.5 mx-auto" />
                  </th>
                  <th className="text-left py-3 px-3 font-semibold">Progress</th>
                  <th className="text-left py-3 px-3 font-semibold">
                    Into WH
                    <div className="text-[9px] text-zinc-400 normal-case tracking-normal font-normal mt-0.5">WeLove</div>
                  </th>
                  <th className="text-left py-3 px-3 font-semibold">
                    Collected
                    <div className="text-[9px] text-zinc-400 normal-case tracking-normal font-normal mt-0.5">by Giavani</div>
                  </th>
                  <th className="text-left py-3 px-3 font-semibold">
                    Expected back
                    <div className="text-[9px] text-zinc-400 normal-case tracking-normal font-normal mt-0.5">to WeLove</div>
                  </th>
                  <th className="text-left py-3 px-4 font-semibold">Updated</th>
                  <th className="w-8" />
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={canBulk ? 13 : 12} className="text-center py-8 text-zinc-400">Loading…</td></tr>
                )}
                {!loading && visibleRecords.length === 0 && (
                  <tr><td colSpan={canBulk ? 13 : 12} className="text-center py-12 text-zinc-400 text-sm">
                    No bath records match your filters.
                    {deliveredHidden > 0 && (
                      <div className="mt-2 text-[11px]">
                        {deliveredHidden} delivered record{deliveredHidden === 1 ? " is" : "s are"} hidden — switch the status filter to &quot;All&quot; to show them.
                      </div>
                    )}
                  </td></tr>
                )}
                {!loading && visibleRecords.map((r) => {
                  const isSelected = selectedIds.has(r.id);
                  const isOverdue = overdueIds.has(r.id);
                  const isUrgent = !!r.is_urgent && r.status !== "delivered";
                  const isDelivered = r.status === "delivered";
                  const rowTint = isUrgent
                    ? "bg-amber-50/60 hover:bg-amber-100/50"
                    : isOverdue
                    ? "bg-amber-50/40 hover:bg-amber-100/40"
                    : isDelivered
                    ? "bg-emerald-50/40 hover:bg-emerald-100/40"
                    : isSelected
                    ? "bg-zinc-50 hover:bg-zinc-100"
                    : "hover:bg-zinc-50";
                  const leftAccent = isUrgent
                    ? "border-l-2 border-l-amber-500"
                    : isOverdue
                    ? "border-l-2 border-l-amber-300"
                    : isDelivered
                    ? "border-l-2 border-l-emerald-400"
                    : "border-l-2 border-l-transparent";

                  return (
                    <tr
                      key={r.id}
                      onClick={() => navigate(`/bath/${r.id}`)}
                      className={`border-t border-zinc-100 cursor-pointer row-in transition-colors ${rowTint} ${leftAccent}`}
                      data-testid={`bath-row-${r.id}`}
                    >
                      {canBulk && (
                        <td className="pl-4 py-3 w-9" onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggle(r.id)}
                            data-testid={`bulk-select-${r.id}`}
                            aria-label="Select row"
                          />
                        </td>
                      )}
                      <td className="py-3 px-4 font-mono text-xs text-zinc-950 font-semibold whitespace-nowrap">
                        <div className="flex items-center gap-1.5">
                          {isUrgent && (
                            <span
                              className="inline-flex items-center justify-center h-4 w-4 rounded-full bg-amber-500 text-white"
                              title={r.urgent_reason ? `Urgent — ${r.urgent_reason}` : "Marked urgent"}
                              data-testid={`urgent-flag-${r.id}`}
                            >
                              <Flame className="h-2.5 w-2.5" strokeWidth={3} />
                            </span>
                          )}
                          {!isUrgent && isOverdue && (
                            <span
                              className="inline-flex items-center justify-center h-4 w-4 rounded-full bg-amber-300 text-zinc-900"
                              title={
                                overdueMode === "past_expected_return"
                                  ? "Overdue — past Expected Back date"
                                  : overdueMode === "either"
                                  ? `Overdue — > ${overdueDays} days with third party or past Expected Back`
                                  : `Overdue — with third party more than ${overdueDays} days`
                              }
                              data-testid={`overdue-flag-${r.id}`}
                            >
                              <Clock className="h-2.5 w-2.5" strokeWidth={3} />
                            </span>
                          )}
                          <span>{r.supplier_po}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4 whitespace-nowrap">
                        <div className="font-mono text-xs text-zinc-900">{r.internal_po || "—"}</div>
                        {r.revised_po && (
                          <div className="font-mono text-[10px] text-amber-700">
                            <span className="opacity-70">rev →</span> {r.revised_po}
                          </div>
                        )}
                        <div className="font-mono text-[11px] text-zinc-500">{r.sales_order || ""}</div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="font-mono text-xs font-semibold text-zinc-900">{r.product_sku}</div>
                        {r.alternate_sku && (
                          <div className="font-mono text-[10px] text-zinc-500">⇄ {r.alternate_sku}</div>
                        )}
                        <div className="text-[11px] text-zinc-500 truncate max-w-[260px]">{r.description}</div>
                      </td>
                      <td className="py-3 px-4 text-xs text-zinc-700 max-w-[180px] truncate">{r.customer_ref || "—"}</td>
                      <td className="py-3 px-4"><StatusBadge status={r.status} /></td>
                      <td className="py-3 px-2 text-center" onClick={(e) => { e.stopPropagation(); setSpecRecord(r); }}>
                        {(() => {
                          const count = (r.spec_images || []).length || (r.spec_image_path ? 1 : 0);
                          if (count > 0) {
                            return (
                              <button
                                type="button"
                                className="inline-flex items-center justify-center h-6 min-w-6 px-1.5 rounded-md bg-emerald-100 text-emerald-700 hover:bg-emerald-200 transition-colors"
                                title={`${count} spec image${count === 1 ? "" : "s"} — click to preview`}
                                data-testid={`spec-indicator-${r.id}`}
                              >
                                <ImageIcon className="h-3 w-3" />
                                <span className="ml-0.5 text-[10px] font-mono font-semibold">{count}</span>
                              </button>
                            );
                          }
                          return (
                            <button
                              type="button"
                              className="inline-flex items-center justify-center h-6 w-6 rounded-md bg-amber-50 text-amber-600 hover:bg-amber-100 transition-colors"
                              title="No spec uploaded — click to add"
                              data-testid={`spec-indicator-${r.id}`}
                            >
                              <AlertTriangle className="h-3 w-3" />
                            </button>
                          );
                        })()}
                      </td>
                      <td className="py-3 px-3"><ProgressDots status={r.status} /></td>
                      <td className="py-3 px-3 text-xs text-zinc-700 font-mono whitespace-nowrap">{formatDateShort(r.date_received)}</td>
                      <td className="py-3 px-3 text-xs text-zinc-700 font-mono whitespace-nowrap">{formatDateShort(r.date_collected)}</td>
                      <td className="py-3 px-3 text-xs text-zinc-700 font-mono whitespace-nowrap">
                        {r.date_returned ? (
                          <span className="text-emerald-700">{formatDateShort(r.date_returned)}</span>
                        ) : (
                          formatDateShort(r.expected_return_date)
                        )}
                      </td>
                      <td className="py-3 px-4 text-xs text-zinc-500 whitespace-nowrap">
                        <div>{formatRelative(r.updated_at)}</div>
                        <div className="text-[10px] text-zinc-400">{r.updated_by}</div>
                      </td>
                      <td className="py-3 px-2 text-zinc-400"><ChevronRight className="h-4 w-4" /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {summary && (
          <div className="mt-3 text-xs text-zinc-500 text-right" data-testid="dashboard-count">
            Showing {visibleRecords.length} of {summary.total} records
            {deliveredHidden > 0 && (
              <button
                type="button"
                onClick={() => setStatusFilter("all")}
                className="ml-2 text-zinc-700 underline-offset-2 hover:underline"
                data-testid="show-delivered-link"
              >
                ({deliveredHidden} delivered hidden — show all)
              </button>
            )}
          </div>
        )}
      </main>

      <CreateRecordDialog
        open={showCreate}
        onOpenChange={setShowCreate}
        thirdParties={thirdParties}
        onCreated={() => load()}
      />

      <BulkUpdateDialog
        open={showBulk}
        onOpenChange={setShowBulk}
        ids={Array.from(selectedIds)}
        thirdParties={thirdParties}
        onDone={() => {
          clearSelection();
          load();
        }}
      />

      <SpecQuickViewDialog
        open={!!specRecord}
        onOpenChange={(v) => !v && setSpecRecord(null)}
        record={specRecord}
      />
    </div>
  );
}
